import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BOTTOM_INDENTS, COLORS, ROUTE_PATHS, TOP_INDENTS } from '@constants';
import { CONTACT_TYPE, NOTIFICATION_ACTIONS } from '@enums';
import { useStore } from '@hooks';
import { Button, Loader, Tooltip, Typography } from '@ui';
import { Checkbox, Col, Divider, Flex, Form, Row } from 'antd';
import * as upperFirst from 'lodash/upperFirst';
import { observer } from 'mobx-react';
import { UNSUBSCRIBE_EXAMPLES } from './constants';
import { BrandName, StyledCheckboxGroup, StyledDivider, StyledWrapper } from './styled';

const Unsubscribe = observer(() => {
    const [complete, setComplete] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [form] = Form.useForm();
    const store = useStore();
    const token = searchParams.get('token');
    const { unsubscribeList, getUnsubscribeList, updateContacts } = store.accountsStore;

    const notificationCategories = Object.entries(NOTIFICATION_ACTIONS).map(([key, value]) => {
        const category = key.toLowerCase();
        return {
            label: `'${upperFirst(category)}' ${UNSUBSCRIBE_EXAMPLES[category] || ''}`,
            value: value,
            disabled: false,
            'data-e2e': `unsubscribe-page_${category}_checkbox`
        };
    });

    useEffect(() => {
        if (token) {
            (async () => {
                setFetching(true);
                await getUnsubscribeList(token);
                setFetching(false);
            })();
        } else {
            setFetching(false);
        }
    }, [token]);

    const saveContacts = async (values) => {
        setLoading(true);
        const dataToSend = unsubscribeList.reduce((acc, item) => {
            const currentNotifications = item.notifications || [];
            const selectedToUnsubscribe = values[item.contactId] || [];
            const remainingNotifications = currentNotifications.filter(
                (notification) => !selectedToUnsubscribe.includes(notification)
            );
            if (
                item.type === CONTACT_TYPE.FOR_KLICKLY &&
                !remainingNotifications.includes(NOTIFICATION_ACTIONS.ACCOUNT)
            ) {
                remainingNotifications.push(NOTIFICATION_ACTIONS.ACCOUNT);
            }
            acc[item.contactId] = remainingNotifications;
            return acc;
        }, {});
        await updateContacts({ token, data: dataToSend });
        setLoading(false);
        setComplete(true);
    };

    if (!token) {
        return (
            <StyledWrapper>
                <Col style={TOP_INDENTS.XL_PLUS} span={24}>
                    <Row align='middle'>
                        <Col span={24}>
                            <Typography.Title data-e2e='unsubscribe-page_error_text' align='center' level={1}>
                                Invalid unsubscribe link
                            </Typography.Title>
                        </Col>
                    </Row>
                </Col>
            </StyledWrapper>
        );
    }

    if (fetching) {
        return <Loader />;
    }

    return (
        <StyledWrapper>
            <Col style={TOP_INDENTS.XL_PLUS} span={24}>
                <Row align='middle'>
                    <Col span={24}>
                        <Typography.Title data-e2e='unsubscribe-page_title_text' align='center' level={1}>
                            We&apos;re sad to see you go!
                        </Typography.Title>
                    </Col>
                </Row>
            </Col>
            <StyledDivider />
            <Col span={24} style={BOTTOM_INDENTS.XL_PLUS}>
                <Row justify='center'>
                    <Col md={16}>
                        <Typography.Title data-e2e='unsubscribe-page_description_text' align='center' level={3}>
                            It&apos;s not the same without you... We hate goodbyes, but if you change your mind, you
                            know where you can find us.
                        </Typography.Title>
                    </Col>
                </Row>
            </Col>
            {complete ? (
                <Flex gap={25} vertical justify='center' align='center'>
                    <Typography.Title data-e2e='unsubscribe-page_success_text' level={2}>
                        You’ve been successfully unsubscribed!
                    </Typography.Title>
                    <Button onClick={() => navigate(ROUTE_PATHS.HOME)} type='primary'>
                        Go to dashboard
                    </Button>
                </Flex>
            ) : (
                <Flex vertical justify='center'>
                    {unsubscribeList.length ? (
                        <React.Fragment>
                            <Form
                                onFinish={saveContacts}
                                initialValues={unsubscribeList.reduce((initialValues, item) => {
                                    const baseNotifications = item.notifications || [];
                                    if (item.type === CONTACT_TYPE.FOR_KLICKLY) {
                                        initialValues[item.contactId] = baseNotifications.filter(
                                            (notification) => notification !== NOTIFICATION_ACTIONS.ACCOUNT
                                        );
                                    } else {
                                        initialValues[item.contactId] = baseNotifications;
                                    }
                                    return initialValues;
                                }, {})}
                            >
                                {unsubscribeList?.map((item) => {
                                    const accountNotifications = item.notifications || [];

                                    const accountNotificationCategories = notificationCategories.map((notification) => {
                                        const isUncheckedFromApi = !accountNotifications.includes(notification.value);
                                        const isAccountForKlickly =
                                            item.type === CONTACT_TYPE.FOR_KLICKLY &&
                                            notification.value === NOTIFICATION_ACTIONS.ACCOUNT;

                                        const isDisabled = isUncheckedFromApi || isAccountForKlickly;

                                        let tooltipMessage = null;
                                        if (isDisabled && isAccountForKlickly) {
                                            tooltipMessage =
                                                "You can't unsubscribe from 'Accounts' notifications for Klickly contacts.";
                                        }

                                        if (isDisabled && isUncheckedFromApi) {
                                            tooltipMessage = 'Already unsubscribed.';
                                        }

                                        return {
                                            ...notification,
                                            disabled: isDisabled,
                                            tooltip: tooltipMessage
                                        };
                                    });
                                    return (
                                        <React.Fragment key={item.contactId}>
                                            <BrandName style={BOTTOM_INDENTS.XS}>
                                                <Typography.Text
                                                    className='brand-name-text'
                                                    data-e2e={`unsubscribe-page_brand-name_${item.contactId}`}
                                                >
                                                    Unsubscribe {item.companyName} from:
                                                </Typography.Text>
                                            </BrandName>
                                            <Form.Item name={item.contactId}>
                                                <StyledCheckboxGroup
                                                    onChange={(checkedValues) => {
                                                        form.setFieldsValue({ [item.contactId]: checkedValues });
                                                    }}
                                                >
                                                    {accountNotificationCategories.map((item) => {
                                                        const checkbox = (
                                                            <Checkbox
                                                                style={BOTTOM_INDENTS.XS}
                                                                value={item.value}
                                                                disabled={item.disabled}
                                                                data-e2e={item['data-e2e']}
                                                            >
                                                                {item.label}
                                                            </Checkbox>
                                                        );

                                                        return item.tooltip ? (
                                                            <Tooltip
                                                                key={item.value}
                                                                title={item.tooltip}
                                                                trigger='hover'
                                                            >
                                                                {checkbox}
                                                            </Tooltip>
                                                        ) : (
                                                            <React.Fragment key={item.value}>{checkbox}</React.Fragment>
                                                        );
                                                    })}
                                                </StyledCheckboxGroup>
                                            </Form.Item>
                                            {unsubscribeList.indexOf(item) < unsubscribeList.length - 1 && <Divider />}
                                        </React.Fragment>
                                    );
                                })}
                                <Flex justify='center'>
                                    <Button
                                        data-e2e='unsubscribe-page_unsubscribe_btn'
                                        loading={loading}
                                        htmlType='submit'
                                        type='primary'
                                        style={{ ...TOP_INDENTS.M, ...BOTTOM_INDENTS.M }}
                                    >
                                        Unsubscribe
                                    </Button>
                                </Flex>
                            </Form>
                        </React.Fragment>
                    ) : (
                        <Flex justify='center' style={BOTTOM_INDENTS.M}>
                            <Typography.Text
                                data-e2e='unsubscribe-page_no-subscriptions_text'
                                color={COLORS.$gray60Black}
                            >
                                You don&apos;t have any subscriptions
                            </Typography.Text>
                        </Flex>
                    )}
                </Flex>
            )}
        </StyledWrapper>
    );
});

export default Unsubscribe;
